import React from 'react'
import { Box, Typography } from '@mui/material'
import WarningIcon from '@mui/icons-material/Warning'
import ErrorIcon from '@mui/icons-material/Error'
import InfoIcon from '@mui/icons-material/Info'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'

type Severity = 'warning' | 'error' | 'success' | 'info'

interface CustomAlertProps {
  message?: string
  title?: string
  severity?: Severity
  icon?: React.ReactElement
  maxWidth?: number | string
  classes?: {
    root?: string
    icon?: string
    message?: string
    title?: string
  }
}

const CustomAlert: React.FC<CustomAlertProps> = ({
  message = 'Error al intentar obtener la información.',
  title,
  severity = 'warning',
  icon,
  maxWidth,
  classes: customClasses
}) => {
  const severityStyles = {
    warning: {
      root: { backgroundColor: '#fff3e0', border: '1px solid #ffc107' },
      icon: { color: '#ff9800' },
      text: { color: '#e79f31' }
    },
    error: {
      root: { backgroundColor: '#ffebee', border: '1px solid #f44336' },
      icon: { color: '#f44336' },
      text: { color: '#c62828' }
    },
    success: {
      root: { backgroundColor: '#e8f5e9', border: '1px solid #4caf50' },
      icon: { color: '#4caf50' },
      text: { color: '#5C8E2F' }
    },
    info: {
      root: { backgroundColor: '#e3f2fd', border: '1px solid #2196f3' },
      icon: { color: '#2196f3' },
      text: { color: '#1565c0' }
    }
  } as const

  // Mapeo de íconos por defecto según severity
  const defaultIcons: Record<Severity, React.ReactElement> = {
    warning: <WarningIcon />,
    error: <ErrorIcon />,
    success: <CheckCircleIcon />,
    info: <InfoIcon />
  }

  const IconComponent = icon || defaultIcons[severity]

  return (
    <Box
      mt={1}
      className={customClasses?.root}
      sx={(theme) => ({
        display: 'flex',
        alignItems: 'center',
        borderRadius: theme.shape.borderRadius,
        p: theme.spacing(2),
        width: '100%',
        boxSizing: 'border-box',
        ...severityStyles[severity].root,
        maxWidth
      })}
    >
      <Box
        component='span'
        className={customClasses?.icon}
        sx={(theme) => ({
          mr: theme.spacing(2),
          fontSize: 24,
          flexShrink: 0,
          ...severityStyles[severity].icon
        })}
      >
        {IconComponent}
      </Box>
      <Box
        className={customClasses?.message}
        sx={{
          fontSize: '1rem',
          lineHeight: 1.5,
          fontWeight: 700,
          ...severityStyles[severity].text
        }}
      >
        {title && (
          <Box
            className={customClasses?.title}
            sx={(theme) => ({
              fontWeight: 1000,
              mb: theme.spacing(0.5),
              ...severityStyles[severity].text
            })}
          >
            <Typography>{title}</Typography>
          </Box>
        )}
        <Typography>{message}</Typography>
      </Box>
    </Box>
  )
}

export default CustomAlert
